// Client for the xenscope-commercial-api console API (10-serverless/100-api).
// Copied from xenquote's console/src/api.ts: every authenticated call goes through
// useApi(), which takes the Cognito ID token from AuthContext and sends it as a
// Bearer token. API Gateway's JWT authoriser checks it against our own pool
// (015-auth); handlers take the company from its custom:companyId claim.
import { useMemo } from "react";
import { useAuth } from "./auth/AuthContext";
import { config } from "./config";
import type { InstallerBlocks } from "./installer";

// ─── Account → Profile (functions/profile) ────────────────────────────────────

/** Company details: Account → Profile. */
export interface CompanyDetails {
  companyName: string;
  tagline: string;
  phone: string;
  email: string;
  website: string;
  address: string;
  companyNumber: string;
  vatNumber: string;
  privacyUrl: string;
}

/** Account → Profile: you, and your company. */
export interface CompanyProfile {
  user: {
    firstName: string;
    lastName: string;
    email: string;
    pictureUrl?: string;
  }; // pictureUrl: SSO avatar
  company: CompanyDetails;
  ownerEmail: string;
  // Where the company works from: Find's distance bands are measured from it (only the postcode comes back)
  base?: { postcode: string };
}

// ─── Dashboard summary (functions/account) ────────────────────────────────────

/** GET /v1/account: what the dashboard shows. plan null = no plan yet. */
export interface AccountSummary {
  company: { companyId: string; companyName: string };
  role: TeamRole;
  plan: { name: string; creditsPerMonth: number } | null;
  credits: { balance: number };
  buildings: { held: number; packsInProgress: number; won: number };
  coverageSet: boolean; // Local Authority Districts saved and locked
}

// ─── Account → Coverage & filters (functions/coverage) ─────────────────────────

export type ElectricityData = "actual" | "any";
/** Public buildings, private business premises, or both (private commercial, PC4.2) */
export type OwnershipFilter = "any" | "public" | "private";

/** Which councils the company works in and which buildings it wants (common/coverage.py). */
export interface Coverage {
  councils: string[]; // ONS local authority codes, up to options.limits.councils
  filters: {
    sizeBands: string[];
    sectors: string[];
    maxPaybackYears: number | null;
    electricityData: ElectricityData;
    includeOverdue: boolean;
    ownership?: OwnershipFilter; // "any" when absent
  };
  weeklyEmail: boolean;
}

/** The choices the form offers (from the API, so the console and the rules never drift). */
export interface CoverageOptions {
  councils: { code: string; name: string }[];
  sizeBands: { key: string; label: string }[];
  sectors: { key: string; label: string }[];
  electricityData: { key: ElectricityData; label: string }[];
  // Sent only where private properties are offered; without it the Filters page has no Public / Private choice
  ownership?: { key: OwnershipFilter; label: string }[];
  limits: { councils: number; payback: [number, number] }; // councils: this company's limit (5 unless its plan says more)
}

export interface CoverageResponse {
  coverage: Coverage;
  options: CoverageOptions;
  canEdit: boolean; // owners and admins
  councilsLockedAt: string | null; // set on the first save; saved councils can't be removed after (more can be added up to the limit)
  councilsLockLog?: { councils: string[]; at: string; by: string }[]; // every save that locked councils
  updatedAt: string | null;
  updatedBy: string | null;
}

// ─── Find buildings (functions/buildings) ─────────────────────────────────────

/** One anonymous shortlist entry: no name, address, UPRN or exact figures (PORTAL-WORKFLOW.md). */
export interface ShortlistBuilding {
  ref: string; // opaque reference, used to preview and reserve
  // Neil, 11 Oct 2026: "make it so that they can't guess too much". No floor area or electricity
  // bands, no overdue or older-data flags; where too few share the card's combination, the API
  // shows a generic building, and payback, grid, roof and rating "Not shown" (common/shortlist.py)
  sector: string | null; // null on a generic card ("Public building" / "Business premises")
  buildingType: string; // the broad sector ("Education building"), or "Public building" / "Business premises"
  ownership?: Ownership; // public or private (absent: public)
  district: string;
  size: string;
  sizeLabel: string;
  payback: string | null; // indicative band, e.g. "6–7 years", or "Not shown" (anonymity rule)
  electricityData: "actual" | "estimated";
  credits: number;
  opportunity?: ShortlistBand<OpportunityBand> | null; // against every building covered; null when not ranked
  grid?: ShortlistBand<GridBand | "hidden">; // spare grid capacity for the area around it, never the substation or figures
  // Four more (Neil, 11 Oct 2026). Roof and energy rating show "hidden" ("Not shown") where too few
  // buildings share the card's combination to stay anonymous
  solar?: ShortlistBand<SolarBand>; // solar recorded here / none recorded (never "none") / not known
  roof?: ShortlistBand<RoofBand | "not-known" | "hidden">; // a band, never the roof area
  energyRating?: ShortlistBand<EnergyBand | "not-known" | "hidden">; // A–G
  distance?: ShortlistBand<DistanceBand | "not-known"> | null; // from the base postcode; null when none is set
}

/** The search bands on every shortlist row and preview (Neil, 11 Oct 2026): a key and its label only. */
export type OpportunityBand = "top" | "strong" | "good" | "fair";
export type GridBand = "good" | "limited" | "constrained" | "not-checked";
export interface ShortlistBand<K extends string> {
  band: K;
  label: string; // e.g. "Top 10%", "Good grid capacity nearby"
}
/** Find's grid filter: good capacity only, or everything except constrained. */
export type GridFilter = "good" | "not-constrained";
export type SolarBand = "yes" | "none-recorded" | "not-known";
export type RoofBand = "small" | "medium" | "large" | "very-large"; // smallest first
export type EnergyBand = "A" | "B" | "C" | "D" | "E" | "F" | "G";
export type DistanceBand = "within-10" | "10-25" | "25-50" | "50-100" | "over-100"; // nearest first

// roof: largest roof band first · nearest: nearest distance band first (needs a base postcode)
export type ShortlistSort = "score" | "smallest" | "district" | "type" | "grid" | "roof" | "nearest";

/**
 * Find buildings' temporary filters. Each starts from the saved coverage (unset = as saved) and
 * can go either way for this view, except districts, which can only narrow.
 */
export interface ShortlistFilters {
  districts?: string[];
  sizes?: string[];
  sectors?: string[];
  maxPayback?: number | "any" | null;
  data?: ElectricityData | null;
  ownership?: OwnershipFilter | null; // unset = as saved ("any" by default)
  minOpportunity?: OpportunityBand | null; // this band or better (Find only, never saved)
  grid?: GridFilter | null; // Find only, never saved
  hideSolar?: boolean | null; // hide buildings where solar may already be fitted (the default: Neil, 14 Oct 2026); false shows them
  minRoof?: RoofBand | null; // this roof band or larger
  rating?: EnergyBand | null; // this energy rating or worse
  maxDistance?: Exclude<DistanceBand, "over-100"> | null; // this band or nearer (with a base postcode)
}

/** Every choice the filters offer, and what's saved (pre-ticked). */
export interface ShortlistFacets {
  districts: { key: string; label: string }[]; // the saved districts only (fixed)
  sizes: { key: string; label: string }[];
  sectors: { key: string; label: string }[];
  saved: {
    sizes: string[];
    sectors: string[];
    maxPayback: number | null;
    electricityData: ElectricityData;
    ownership?: OwnershipFilter;
  };
  // How many matching properties are public and private (absent where private properties aren't offered)
  ownership?: { public: number; private: number };
  // Find's own search bands, best first, and a one-line explanation of each
  opportunityBands?: { key: OpportunityBand; label: string }[];
  gridBands?: { key: GridBand; label: string }[];
  explain?: { opportunity: string; grid: string; solar?: string; roof?: string; energyRating?: string; distance?: string; hidden?: string };
  roofBands?: { key: RoofBand; label: string }[];
  energyBands?: { key: EnergyBand; label: string }[];
  distanceBands?: { key: DistanceBand; label: string }[];
  base?: { postcode: string } | null; // null: no base postcode yet, so no distances
}

export interface Shortlist {
  coverageSet: boolean; // false until Local Authority Districts are saved
  total: number;
  page?: number;
  pageSize?: number;
  sort?: ShortlistSort;
  generatedAt?: string;
  buildings: ShortlistBuilding[];
  facets?: ShortlistFacets;
}

/**
 * A building's anonymous preview (GET /v1/buildings/{ref}): the shortlist row plus
 * indicative outputs as ranges and what the pack holds. "unavailable" means another
 * installer holds it, and then only ref and status come back.
 */
export interface BuildingPreview extends Partial<ShortlistBuilding> {
  ref: string;
  // yours: exclusivity running · lapsed: yours, ended, nobody else has taken it yet
  status: "available" | "yours" | "lapsed" | "unavailable";
  exclusivity?: { until: string; canRenew: boolean }; // the holder only
  // Ranges for the size band's systems ("42–128 MWh a year"), never this building's own figures
  indicative?: {
    generation: string | null;
    yearOneSaving: string | null;
    co2: string | null;
  };
  packContents?: string[];
}

/** An order just placed (POST /v1/buildings/{ref}/order). */
export interface PlacedOrder {
  orderId: string;
  status: "ordered" | "renewed";
  orderNumber?: string | null; // XSC-1001 … (orders only)
  orderedAt: string;
  exclusiveUntil: string; // 6 months from the order (or from the end, for a renewal)
  credits: number;
  /** The property just bought (orders only): theirs now, so named for the link to it */
  property?: { name?: string | null; town?: string | null; postcode?: string | null };
}

// ─── Targets: request a building by address (#36) ─────────────────────────────

export interface PlaceSuggestion {
  placeId: string;
  main: string;
  secondary: string;
}

/** The terms of purchase (100-api common/terms.py); statement has a {credits} placeholder. */
export interface PurchaseTermsDoc {
  version: string;
  title: string;
  intro: string;
  clauses: { heading: string; text: string }[];
  statement: string;
  // For an estimated property: the standard and method, shown before its own tick (Neil, 14 Oct 2026)
  estimate?: { version: string; title: string; method: string[]; statement: string };
}

/** The customer's tick for one property, sent with the order or renewal. */
export interface PurchaseTermsAccepted {
  accepted: true;
  version: string;
  credits: number;
  /** Estimated electricity use acknowledged (required for an estimated property) */
  estimateAccepted?: boolean;
}

/** Account → Pricing (#38): the company's own prices and assumptions for its packs. */
export interface Pricing {
  bands: {
    upToKwp: number;
    perKwp: number;
    parts?: { panels: number; inverters: number; mounting: number; electrical: number; labour: number; margin: number };
  }[];
  itemised: boolean;
  access: { mode: "scaffold" | "mewp" | "fixed" | "none"; scaffoldPerM: number; mewpPerDay: number; kwpPerDay: number; fixed: number };
  structuralSurvey: number;
  designFee: number;
  g99Application: number;
  g99WitnessTest: number;
  g99WitnessKw: number;
  monitoring: number;
  roofLeftClearPct: number;
  panelWatts: number;
  panelModel: string;
  importPence: number;
  exportPence: number;
  priceInflationPct: number;
  systemLossesPct: number;
  omPerKwp: number;
  vatPct: number;
}

/** One of the company's pricing models (or our standard prices, id "standard"). */
export interface PricingModel {
  id: string;
  name: string;
  pricing: Pricing;
  isDefault: boolean;
  /** A new UUID on every change to its name or prices */
  versionId?: string;
  /** The version number people see: v1, v2 … */
  version?: number;
  updatedAt?: string | null;
  updatedBy?: string | null;
  /** What the model is for, in a line or two (the models page's cards) */
  description?: string;
  /** When and by whom it was made (absent for models made before 14 Oct 2026) */
  createdAt?: string | null;
  createdBy?: string | null;
}

export interface PricingResponse {
  models: PricingModel[];
  standard: { id: string; name: string; pricing: Pricing; description?: string };
  canEdit: boolean;
  /** The model just created or saved */
  id?: string;
}

/** One property to pick from a site's buildings or a postcode's list: its name only. */
export interface TargetChoice {
  ref: string;
  name: string;
  /** Public building or private business premises: a badge on the choice (Neil, 14 Oct 2026) */
  ownership?: Ownership;
}

/** What we can say about a picked place: high level only, never figures or sources. */
export interface TargetCheck {
  place: { name: string; address: string; postcode: string };
  result:
    | {
        status: "available";
        ref: string;
        buildingType: string;
        district: string;
        sizeLabel: string;
        electricityData: "actual" | "estimated";
        olderData?: boolean; // a named target only (the customer already knows which building it is)
        credits: number;
      }
    | { status: "outside"; ref: string; buildingType?: string; district: string; yourDistricts: string[] }
    | { status: "yours" | "unavailable" | "not-found" }
    // Ruled out by our team, and why
    | { status: "excluded"; reason?: "existing-solar" | "unsuitable" | string }
    // Not one we can offer, and why (never the source)
    | { status: "not-suitable"; reason: "renewables" | "too-small" | "no-data" | "not-yet" | string }
    // A site with several of our buildings (a school's blocks): the customer picks which
    | { status: "choose"; choices: TargetChoice[]; postcode?: string };
  creditBalance?: number;
}

// ─── My buildings (functions/my_buildings) ────────────────────────────────────

/** Progress of a pack: set by our team as they prepare it. */
export type PackStatus = "ordered" | "in-progress" | "completed" | "failed";

/** The building's details, shown from the moment it's ordered (never before). */
export interface OwnedProperty {
  name: string;
  addressLines?: string[];
  town: string | null;
  postcode: string | null;
  district: string;
  buildingType: string;
  sector?: string;
  floorM2?: number | null;
  electricityKwh?: number | null; // a year
  electricityData?: "actual" | "estimated";
  overdue?: boolean; // public only: never shown for a private property
  indicativeKwp?: number | null;
  paybackYears?: number | null;
  sizeLabel: string | null;
  mapUrl?: string | null;
  location?: { lat: number; lng: number } | null; // the postcode's centre (dashboard map pin)
  // Private commercial (BUILD-PLAN-PRIVATE-COMMERCIAL.md, data contract). Absent on older orders: public
  ownership?: Ownership;
  /** How a private building's electricity use was estimated, in neutral words only */
  estimate?: { confidence: "high" | "medium" | "low"; basis: "size-and-use" } | null;
  /** The businesses under one roof, when several share the building (absent for a single unit) */
  units?: PropertyUnit[] | null;
  /** Who owns the building, with the attribution its licence requires (shown in small print) */
  owner?: PropertyOwner | null;
}

/** Public sector building, or private business premises. */
export type Ownership = "public" | "private";

/** One business in a multi-let building. */
export interface PropertyUnit {
  name: string;
  use: string; // a plain description, e.g. "Warehouse and premises"
  floorM2: number | null;
  elecKwh: number | null; // estimated electricity a year
}

/** The building's owner, once ordered. */
export interface PropertyOwner {
  name: string;
  companyNumber: string | null;
  tenure: string | null; // "freehold" | "leasehold"
  overseas: boolean;
  attribution: string;
}

export interface OwnedBuilding {
  orderId: string;
  orderNumber: string | null; // XSC-1001 …: what people quote
  source: "shortlist" | "target"; // from Find properties, or requested by address (Targets)
  status: PackStatus;
  orderedAt: string;
  credits: number;
  property: OwnedProperty;
  // running: exclusive to you · ended: you can still renew · taken: another installer has it now
  exclusivity: {
    state: "running" | "ended" | "taken";
    until: string | null;
    canRenew: boolean;
  };
  ref: string; // this company's reference, for Renew on the property page
}

/** One preliminary check on an ordered property (the API's common/checks.py). */
export interface PreliminaryCheck {
  key: string;
  label: string;
  status: "passed" | "warning" | "checking" | "failed";
  detail: string;
  /** The solar check only: the AI roof check's own answer */
  ai?: {
    result: "yes" | "no" | "unsure";
    /** The building couldn't be pinned down exactly on its site: our team looks at the roof too */
    locationNote?: boolean;
    coverage: string | null;
    confidence: number;
    summary: string | null;
    /** The roof described for an installer (never how it was seen) */
    roof?: {
      shape: "pitched" | "flat" | "mixed" | "curved" | "unclear" | null;
      layout: string | null;
      covering: string | null;
      features: string[];
      condition: string | null;
      access?: string | null;
      asbestos?: "possible" | "unlikely" | null;
    } | null;
    scope: "building" | "site";
  };
}

/** Roof and site (100-api common/site.py, common/grants.py). */
export type AirportResult = "final-approach" | "under-approach" | "within-distance" | "clear";

/** One airport near the building: distance, direction, and where the building sits against its nearest approach */
export interface AirportCheck {
  name: string;
  kind: "airport" | "military" | "airfield";
  /** Where the airfield is (its reference point), for the airports map */
  lat: number;
  lng: number;
  km: number;
  direction: string;
  /** Each runway: its two ends (id like "09", position, the landing heading), approx when its position is estimated */
  runways?: { ends: { id: string; lat: number; lng: number; headingT: number | null }[]; approx: boolean }[];
  result: AirportResult;
  closest: { runway: string; from: string; alongKm: number; offsetKm: number; halfWidthKm: number; inside: boolean; heightFt: number; approx: boolean } | null;
}

/** Around the building (100-api common/site_extras.py): worked out once, in the background */
export interface SiteExtras {
  outdoor?: { checked: boolean; carParks: { areaM2: number; spaces: number; canopyKwp: number }[]; carParkTotalM2: number; canopyKwp: number; openGroundM2: number; groundKwp: number; note: string };
  flood?: { checked: boolean; zone: "1" | "2" | "3" | null; note: string };
  airports?: {
    nearest?: { name: string; km: number } | null;
    withinKm: { name: string; km: number }[];
    glareCheck: boolean;
    note: string;
    /** The aviation check, with its working (older checks have only the fields above) */
    result?: AirportResult;
    airports?: AirportCheck[];
    airstrips?: { name: string; km: number; direction: string; lat?: number; lng?: number }[];
    /** False when the check couldn't run */
    checked?: boolean;
    rules?: { assessKm: number; approachKm: number; glideDeg: number; splayPct: number; airstripKm: number };
  };
  solarNearby?: { checked: boolean; count: number; named: { name: string; km: number; operator?: string }[]; note: string };
}

export interface SiteBlock {
  /** Car parks and open ground, flood, airports, solar nearby; null while they're being checked */
  extras?: SiteExtras | null;
  roof: {
    planes: number;
    roofM2: number | null;
    footprintM2: number | null;
    flatPct: number;
    pitchRange: [number, number] | null;
    faces: { face: string; pct: number }[];
    southFacingPct: number;
    sunnyPct: number | null;
    maxPanels: number | null;
    capacityKwp: number | null;
    storeys: number | null;
    heightM: number | null;
    heightHow: string | null;
    access: string | null;
  } | null;
  constraints?: { checked: boolean; listed: { name: string; grade: string | null }[]; conservationAreas: string[]; monuments: string[] };
  grid?: {
    covered: boolean;
    operator?: string;
    substation?: string;
    level?: string;
    voltageKv?: number;
    headroomMw?: number | null;
    colour?: string | null;
    upstreamConstraint?: boolean;
  };
  funding: { key: string; name: string; what: string; who: string; how?: string; todo?: string; url: string }[];
  fundingCheckedOn: string;
}

/** About the property: the AI research once approved by our team (never its sources). */
export interface AboutFacts {
  owner: string | null;
  ownerType: string | null;
  operator: string | null;
  operatorNote: string | null;
  description: string | null;
  openingHours: string | null;
  openDaysPerWeek: number | null;
  visitors: string | null;
  opened: string | null;
  recentWorks: string | null;
  heating: string | null;
  whyNow: { point: string; date?: string }[];
  energyPlans: { point: string; date?: string }[];
  /** Who decides: public, work-related facts only, each to be verified before contact */
  funding?: { scheme: string; what: string | null; amount: string | null; status?: string; date?: string }[];
  planning?: { what: string; status?: string; date?: string; reference?: string }[];
  electrification?: { point: string }[];
  people?: { name: string; role: string; organisation: string | null; why: string | null; seen?: string; toVerify: boolean }[];
  /** Solar already installed, contracted or planned for the building, if the research found it */
  existingSolar?: { status: "installed" | "contracted" | "planned"; detail: string | null; date: string | null } | null;
  /** The building's history: dated events (built, extended, roof replaced, sold), oldest first */
  history?: { date?: string; event: string }[];
  roof?: { age: string | null; covering: string | null; condition: string | null; lastWorks: string | null } | null;
  /** Everything renewable or low-carbon at the building, past to planned */
  renewables?: {
    type: string;
    status: "installed" | "contracted" | "planned" | "proposed" | "removed";
    detail: string;
    size: string | null;
    date: string | null;
    contractor: string | null;
  }[];
  /** Freehold or leasehold, the landlord, when the lease ends */
  tenure?: { holding: "freehold" | "leasehold" | null; landlord: string | null; leaseEnds: string | null; detail: string | null } | null;
  /** How they buy: must they tender, the frameworks, when budgets are set */
  buying?: { mustTender: boolean | null; frameworks: string[]; budgetCycle: string | null; detail: string | null } | null;
  /** The energy supply contract */
  energyContract?: { supplier: string | null; renews: string | null; framework: string | null; detail: string | null } | null;
  /** Compliance pressures: MEES, ESOS, SECR, science-based targets */
  compliance?: { scheme: string; applies: "yes" | "likely" | "no" | "unknown"; detail: string }[];
  /** A private company owner's status */
  companyHealth?: { status: string | null; accountsOverdue: boolean | null; detail: string | null } | null;
  /** What the research looked for and couldn't find, said plainly */
  notFound?: string[];
  /** 2: asked about tenure, buying, the contract, compliance and company health; 1: done before those were added */
  depth?: number;
  /** Tenders and contract awards at the site */
  procurement?: { what: string; status: "tender" | "awarded" | "cancelled" | null; value: string | null; supplier: string | null; date: string | null }[];
}

/** The pricing model a property's figures use, whether it's out of date, the choices and the history. */
export interface PricingStatus {
  applied: { id: string; name: string; versionId: string; version?: number | null; appliedAt?: string | null; appliedBy?: string | null };
  /** The customer's confirmation of the model and version in use; null until they confirm (even the default) */
  confirmed?: { at: string | null; by: string | null } | null;
  /** This property's own electricity rate (from a bill), used over the model's; null when it uses the model's */
  rates?: { importPence: number; by?: string | null; at?: string | null } | null;
  /** The model's version now; null when it has been deleted */
  latestVersionId: string | null;
  /** The model's version number now (v1, v2 …) */
  latestVersion?: number | null;
  outdated: boolean;
  deleted: boolean;
  options: { id: string; name: string; versionId: string; version?: number; isDefault: boolean }[];
  history: { at: string | null; by: string | null; name: string; id: string; versionId: string; version?: number | null; reason: "ordered" | "changed" | "updated" | string; summary: string }[];
}

/** The exported report (the pack PDF) for a completed order. */
export interface ReportState {
  status: "none" | "generating" | "ready" | "failed";
  /** Made on other prices than the figures now use: regenerate it */
  outdated?: boolean;
  pricingModelName?: string | null;
  pricingVersion?: number | null;
  generatedAt?: string | null;
  startedAt?: string | null;
  error?: string;
}

/** A 7-day share link to the latest report (revocable; each download counted). */
export interface ShareLink {
  shareId: string;
  url: string;
  createdAt: string;
  expiresAt: string;
  createdBy?: string;
  sentTo?: string[];
  downloads?: number;
  lastDownloadAt?: string | null;
  reportGeneratedAt?: string | null;
}

/** One red flag on a bought property. */
export interface RedFlag {
  key: string;
  severity: "stop" | "check";
  title: string;
  what: string;
  todo: string;
}

/** Where the AI research is: waiting (starts after our team passes the roof check), checking
 * (with our team), approved, unavailable (rejected, or the order was released). */
export type AboutStatus = "waiting" | "checking" | "approved" | "unavailable";

/** One system size in the pack's model (100-api common/model.py). */
export interface SizeOption {
  kwp: number;
  panels: number;
  gen: number;
  used: number;
  selfUse: number;
  covers: number;
  capex: number;
  year1: number;
  payback: number | null;
  irr: number | null;
  npv: number;
}

/** The pack's model: sizing, the recommended system's return, sensitivity, assumptions. */
export interface PackModelData {
  profile: { key: string; label: string };
  options: SizeOption[];
  recommended: SizeOption & {
    items: { label: string; amount: number }[];
    monthly: { gen: number; demand: number; used: number; export: number }[];
    flows: { year: number; gen: number; saving: number; export: number; om: number; inverter: number; net: number; cum: number }[];
    lcoeP: number | null;
    netBenefit: number;
    co2Year: number;
    co2Life: number;
    priceRange: [number, number];
    /** Carbon reporting: location-based Scope 2 avoided */
    carbon?: { co2Year: number; co2Life: number; scope2Pct: number; factorKgPerKwh: number; note: string };
    /** A battery to store solar that would otherwise be exported */
    battery?: {
      recommended: boolean;
      why: string;
      kwh?: number;
      cost?: number;
      shiftedKwh?: number;
      savingYear1?: number;
      payback?: number | null;
      combined?: { capex: number; payback: number | null; irr: number | null; npv: number };
      selfUseWith?: number;
      replacementYear?: number;
      replacementPct?: number;
    };
  };
  phase1: SizeOption | null;
  sensitivity: { label: string; payback: number | null; irr: number | null; npv: number }[];
  assumptions: {
    importPence: number;
    /** Where the import rate comes from: the pricing model's, or "your rate for this property" */
    importSource?: string;
    exportPence: number;
    inflationPct: number;
    panel: string;
    lossesPct: number;
    degradationPct: number;
    roofLeftClearPct: number;
    omPerKwp: number;
    inverterPerKwp: number;
    inverterYear: number;
    discountPct: number;
    years: number;
    co2KgPerKwh: number;
    maxPayback: number;
    yieldKwhPerKwp: number;
  };
}

/** Energy and the opportunity (100-api common/energy.py): the building's figures and projections. */
export interface EnergyBlock {
  estimated: boolean;
  /** metered: actual readings · meter-estimated: actual records with an estimated reading · benchmark: estimated from size and use (private) */
  basis?: "metered" | "meter-estimated" | "benchmark";
  model?: PackModelData;
  pricingModel: string;
  /** The whole pricing model the figures use, as kept on the order (the report's "Your pricing model" appendix) */
  pricing?: Pricing & { batteryPerKwh?: number; batteryRoundTripPct?: number; batteryCyclesPerYear?: number };
  prices: { importPence: number; gasPence: number; inflationPct: number };
  // benchmark: one entry, flagged estimated (no invented history)
  years: { year: string; kwh: number; cost: number; unusual: boolean; estimated?: boolean }[];
  today?: {
    electricityKwh: number;
    gasKwh: number | null;
    floorM2: number | null;
    electricity: number;
    gas: number | null;
    total: number;
    perM2: number | null;
  };
  changePct?: number | null;
  forecast?: {
    scenarios: { key: string; label: string }[];
    years: { year: string; low: { kwh: number; cost: number }; central: { kwh: number; cost: number }; high: { kwh: number; cost: number } }[];
    totals: { low: number; central: number; high: number };
  };
  ratings?: {
    asRun: string | null;
    asRunScore: number | null;
    design: {
      band: string | null;
      score: number | null;
      heating: string | null;
      ventilation: string | null;
      aircon: boolean | null;
      floorM2: number | null;
    } | null;
  };
  renewablesRecorded?: boolean | null; // null for a benchmark estimate (nothing on record)
  system?: {
    kwp: number;
    roofCapKwp: number | null;
    roofM2: number | null;
    roofLeftClearPct: number;
    generationKwh: number;
    selfUsedPct: number;
    capex: number;
    capexItems: { label: string; amount: number }[];
    savingYear1: number;
    paybackYears: number | null;
    savings25: number;
    co2Tonnes: number;
    coversPct: number;
  };
}

export interface OwnedBuildingDetail extends OwnedBuilding {
  checks?: PreliminaryCheck[];
  energy?: EnergyBlock | null;
  site?: SiteBlock | null;
  /** The approved AI research; until then the page shows placeholders (aboutStatus) */
  about?: AboutFacts | null;
  aboutStatus?: AboutStatus | null;
  /** Anything that could stop ("stop") or change ("check") the job, "stop" first (100-api common/red_flags.py) */
  redFlags?: RedFlag[];
  /** The latest exported report (completed orders) */
  report?: ReportState;
  /** The pricing model the figures use (the property page's Pricing model card) */
  pricingStatus?: PricingStatus;
  /** For the installer: supply, a typical day, tax, roof life, installing, paying, first meeting (installer.ts) */
  installer?: InstallerBlocks;
  solarCheck: "checking" | null; // our roof check spotted possible existing panels; staff are checking
  statusHistory: { status: PackStatus; at: string; note?: string }[];
}

// ─── Account → Users (functions/team) ─────────────────────────────────────────

export type TeamRole = "owner" | "admin" | "member";
export interface TeamMember {
  sub: string;
  email: string;
  name: string;
  role: TeamRole;
  joinedAt: string | null;
  pictureUrl: string;
}
export interface TeamInvite {
  email: string;
  role: "admin" | "member";
  invitedAt: string | null;
  invitedBy: string;
  expiresAt: string | null;
  expired: boolean;
}
export interface Team {
  members: TeamMember[];
  invites: TeamInvite[];
  me: { sub: string; role: TeamRole };
  maxUsers: number;
}

// ─── Transport ────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/** One API call: JSON in and out, Bearer ID token when signed in, ApiError on failure. */
async function request<T>(method: string, path: string, token: string | null, body?: unknown): Promise<T> {
  // No API configured (e.g. .env.local written before 100-api was deployed): say so,
  // rather than fetching from the dev server and failing to parse its HTML page
  if (!config.apiBaseUrl) {
    throw new ApiError(0, "The API isn't set up for this environment yet. Locally, run npm run env:dev once 100-api is deployed, then restart npm run dev.");
  }
  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, json.error ?? json.message ?? `Request failed (${res.status})`);
  return json as T;
}

/** The invite link's details (no sign-in: the token is the proof). */
export function getInvite(token: string): Promise<{
  invite: { email: string; company: string; invitedBy: string; role: TeamRole };
}> {
  return request("GET", `/v1/team/invite?t=${encodeURIComponent(token)}`, null);
}

/** Accept an invite: creates the login in the company with this password. */
export function acceptInvite(token: string, firstName: string, lastName: string, password: string): Promise<{ ok: boolean; email: string }> {
  return request("POST", "/v1/team/invite/accept", null, {
    t: token,
    firstName,
    lastName,
    password,
  });
}

/** Unauthenticated GET (e.g. /health). */
export function apiGet<T>(path: string): Promise<T> {
  return request<T>("GET", path, null);
}

/** Typed calls for signed-in pages (the ID token comes from AuthContext). */
export function useApi() {
  const { idToken } = useAuth();
  return useMemo(
    () => ({
      getAccount: () => request<AccountSummary>("GET", "/v1/account", idToken),
      getProfile: () => request<{ profile: CompanyProfile }>("GET", "/v1/profile", idToken).then((r) => r.profile),
      updateProfile: (patch: { user?: { firstName: string; lastName: string }; company?: CompanyDetails; base?: { postcode: string } }) =>
        request<{ profile: CompanyProfile }>("PUT", "/v1/profile", idToken, patch).then((r) => r.profile),
      // Account → Coverage & filters
      getCoverage: () => request<CoverageResponse>("GET", "/v1/coverage", idToken),
      getPricing: () => request<PricingResponse>("GET", "/v1/pricing", idToken),
      createPricingModel: (name: string, pricing: Pricing, isDefault: boolean, description = "") =>
        request<PricingResponse>("POST", "/v1/pricing", idToken, { name, description, pricing, isDefault }),
      updatePricingModel: (id: string, patch: { name?: string; description?: string; pricing?: Pricing; isDefault?: boolean }) =>
        request<PricingResponse>("PUT", `/v1/pricing/${encodeURIComponent(id)}`, idToken, patch),
      deletePricingModel: (id: string) => request<PricingResponse>("DELETE", `/v1/pricing/${encodeURIComponent(id)}`, idToken),
      // acceptCouncilLock: the user ticked "I understand my councils can't be changed" (first save)
      saveCoverage: (coverage: Coverage, acceptCouncilLock = false) =>
        request<CoverageResponse>("PUT", "/v1/coverage", idToken, {
          ...coverage,
          acceptCouncilLock,
        }),
      // Find buildings
      getShortlist: (sort: ShortlistSort = "score", page = 1, f: ShortlistFilters = {}) => {
        const q = new URLSearchParams({ sort, page: String(page) });
        if (f.districts?.length) q.set("districts", f.districts.join(","));
        if (f.sizes?.length) q.set("sizes", f.sizes.join(","));
        if (f.sectors?.length) q.set("sectors", f.sectors.join(","));
        if (f.maxPayback) q.set("maxPayback", String(f.maxPayback));
        if (f.data) q.set("data", f.data);
        if (f.ownership) q.set("ownership", f.ownership);
        if (f.minOpportunity) q.set("minOpportunity", f.minOpportunity);
        if (f.grid) q.set("grid", f.grid);
        if (f.hideSolar === false) q.set("solar", "any"); // hidden unless asked for (the API's default)
        if (f.minRoof) q.set("minRoof", f.minRoof);
        if (f.rating) q.set("rating", f.rating);
        if (f.maxDistance) q.set("maxDistance", f.maxDistance);
        return request<Shortlist>("GET", `/v1/buildings?${q}`, idToken);
      },
      /** One building's preview and the company's credit balance */
      getBuilding: (ref: string) => request<{ preview: BuildingPreview; creditBalance?: number }>("GET", `/v1/buildings/${encodeURIComponent(ref)}`, idToken),
      /** Order its pack: holds the building and spends credits (409 taken, 402 not enough credits) */
      /** The terms every purchase must accept (shown in the order and renewal dialogs) */
      getPurchaseTerms: () => request<{ terms: PurchaseTermsDoc }>("GET", "/v1/terms/purchase", idToken).then((r) => r.terms),
      // Orders start on the company's default pricing model; the property page confirms or changes it
      orderBuilding: (ref: string, terms: PurchaseTermsAccepted) =>
        request<{ order: PlacedOrder }>("POST", `/v1/buildings/${encodeURIComponent(ref)}/order`, idToken, { terms }).then((r) => r.order),
      /** Keep a building another 6 months for the same credits (from 30 days before the end) */
      renewBuilding: (ref: string, terms: PurchaseTermsAccepted) =>
        request<{ order: PlacedOrder }>("POST", `/v1/buildings/${encodeURIComponent(ref)}/renew`, idToken, { terms }).then((r) => r.order),
      // Targets
      searchTargets: (q: string, session: string) =>
        request<{ suggestions: PlaceSuggestion[] }>("GET", `/v1/targets/search?${new URLSearchParams({ q, session })}`, idToken).then((r) => r.suggestions),
      checkTarget: (placeId: string, session: string) => request<TargetCheck>("POST", "/v1/targets/check", idToken, { placeId, session }),
      /** Check one property picked from a choice list by its reference */
      // The postcode goes with the reference: a private property is found again at its postcode
      checkTargetRef: (ref: string, postcode?: string) => request<TargetCheck>("POST", "/v1/targets/check", idToken, { ref, postcode }),
      /** Every property we can prepare a pack for at a full postcode (names only) */
      lookupPostcode: (postcode: string) =>
        request<{ postcode: string; choices: TargetChoice[] }>("GET", `/v1/targets/postcode?${new URLSearchParams({ postcode })}`, idToken),
      /** Outside the company's districts: ask the order desk for a custom quote */
      requestTargetQuote: (ref: string, postcode?: string) =>
        request<{ requested: boolean }>("POST", `/v1/targets/${encodeURIComponent(ref)}/quote`, idToken, { postcode }),
      orderTarget: (ref: string, postcode: string | undefined, terms: PurchaseTermsAccepted) =>
        request<{ order: PlacedOrder }>("POST", `/v1/targets/${encodeURIComponent(ref)}/order`, idToken, { postcode, terms }).then(
          (r) => r.order,
        ),
      // My buildings
      listMyBuildings: () => request<{ buildings: OwnedBuilding[] }>("GET", "/v1/my-buildings", idToken).then((r) => r.buildings),
      getMyBuilding: (orderId: string) =>
        request<{ building: OwnedBuildingDetail }>("GET", `/v1/my-buildings/${encodeURIComponent(orderId)}`, idToken).then((r) => r.building),
      /** Where the building is, for the satellite map (looked up live; never stored) */
      getBuildingMap: (orderId: string) =>
        request<{
          browserKey: string | null;
          pin: { lat: number; lng: number } | null;
          box: { sw: { lat: number; lng: number }; ne: { lat: number; lng: number } } | null;
        }>("GET", `/v1/my-buildings/${encodeURIComponent(orderId)}/map`, idToken),
      /** The best roof imagery for the building (~10 cm), cached by the API for 29 days */
      getRoofImagery: (orderId: string) =>
        request<
          | {
              imageUrl: string;
              outlineUrl?: string; // the building's exact roof, traced from the roof mask (transparent PNG, same bounds)
              shadeUrl?: string; // the same outline with everything around the roof smoked out (for the map)
              bounds: { south: number; west: number; north: number; east: number };
              quality: string | null;
              date: string | null;
              scope?: "building" | "site"; // site: the record covers several buildings, so the whole site is shown
              pin?: { lat: number; lng: number }; // the point deepest inside the traced roof: where the map's pin goes
            }
          | { imagery: null }
        >("GET", `/v1/my-buildings/${encodeURIComponent(orderId)}/imagery`, idToken),
      /** A 5-minute link to the pack PDF (each download is logged) */
      /** This property's own electricity rate (p/kWh), or null to use its pricing model's */
      setPropertyRates: (orderId: string, importPence: number | null) =>
        request<{ building: OwnedBuildingDetail }>("PUT", `/v1/my-buildings/${encodeURIComponent(orderId)}/rates`, idToken, { importPence }).then(
          (r) => r.building,
        ),
      /** Calculate the property on a pricing model (another one, or the same one's latest version) */
      setPropertyPricing: (orderId: string, modelId: string) =>
        request<{ building: OwnedBuildingDetail }>("PUT", `/v1/my-buildings/${encodeURIComponent(orderId)}/pricing`, idToken, { modelId }).then(
          (r) => r.building,
        ),
      // Export report and share links (completed orders; 100-api functions/reports)
      startReport: (orderId: string) => request<{ report: ReportState }>("POST", `/v1/my-buildings/${encodeURIComponent(orderId)}/report`, idToken).then((r) => r.report),
      getReport: (orderId: string) => request<{ report: ReportState }>("GET", `/v1/my-buildings/${encodeURIComponent(orderId)}/report`, idToken).then((r) => r.report),
      getReportLink: (orderId: string) =>
        request<{ url: string; expiresIn: number }>("GET", `/v1/my-buildings/${encodeURIComponent(orderId)}/report/download`, idToken),
      getShares: (orderId: string) => request<{ shares: ShareLink[] }>("GET", `/v1/my-buildings/${encodeURIComponent(orderId)}/shares`, idToken).then((r) => r.shares ?? []),
      createShare: (orderId: string, email?: string) =>
        request<{ share: ShareLink; emailError?: string }>("POST", `/v1/my-buildings/${encodeURIComponent(orderId)}/shares`, idToken, email ? { email } : {}),
      emailShare: (orderId: string, shareId: string, email: string) =>
        request<{ share: ShareLink }>(
          "POST",
          `/v1/my-buildings/${encodeURIComponent(orderId)}/shares/${encodeURIComponent(shareId)}/email`,
          idToken,
          { email },
        ).then((r) => r.share),
      revokeShare: (orderId: string, shareId: string) =>
        request<{ revoked: string }>("DELETE", `/v1/my-buildings/${encodeURIComponent(orderId)}/shares/${encodeURIComponent(shareId)}`, idToken),
      /** Live counts for any districts and filters (saved or not): the Coverage card. */
      countBuildings: (coverage: Coverage) => request<{ total: number; byDistrict: Record<string, number> }>("POST", "/v1/buildings/count", idToken, coverage),
      // Account → Users
      getTeam: () => request<Team>("GET", "/v1/team", idToken),
      inviteUser: (email: string, role: "admin" | "member") => request<Team>("POST", "/v1/team/invites", idToken, { email, role }),
      resendInvite: (email: string) => request<Team>("POST", "/v1/team/invites/resend", idToken, { email }),
      revokeInvite: (email: string) => request<Team>("POST", "/v1/team/invites/revoke", idToken, { email }),
      setUserRole: (sub: string, role: "admin" | "member") => request<Team>("PUT", `/v1/team/members/${encodeURIComponent(sub)}`, idToken, { role }),
      removeUser: (sub: string) => request<Team>("DELETE", `/v1/team/members/${encodeURIComponent(sub)}`, idToken),
    }),
    [idToken],
  );
}
